'use client'

import {
  ArrowRight,
  Atom,
  Bot,
  Cpu,
  Gift,
  GitFork,
  LayoutDashboard,
  Logs,
  Microscope,
  PencilRuler,
  Play,
  Plus,
  Pyramid,
  Sparkles,
  Store,
  Terminal,
} from 'lucide-react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'

import { getWorkflowsList, type WorkflowsListItem } from '@/api/agent/agentApi'
import Py2dMolMiniPreview from '@/app/(authenticated)/library/components/Py2dMolMiniPreview'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/contexts/AuthContext'
import { useNewsModal } from '@/contexts/NewsModalContext'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { useFeatureAccessBatch } from '@/hooks/use-feature-access-batch'
import { cn } from '@/lib/utils'
import { fetchWithAuth } from '@/utils/api-error-handler'
import { formatRelative } from '@/utils/relative-time'
import { appendWorkspaceScopeToPath } from '@/utils/workspace-scope'

const API_URL = process.env.NEXT_PUBLIC_API_URL?.replace(/\/$/, '')
const MAX_HOME_PREVIEW_CARDS = 3
const MAX_HOME_PREVIEW_CANDIDATES = 6

type AgentSession = {
  id: string
  status: string
  profile_name?: string | null
  runtime?: string | null
  cpu_units?: number | null
  memory_mib?: number | null
  gpu_model?: string | null
  created_at: string
  updated_at: string
}

type ProjectItem = {
  id: string
  title: string
  targets_count?: number | null
  experiments_count?: number | null
  created_at?: string | null
  updated_at?: string | null
}

type ProjectListResponse = {
  data: ProjectItem[]
  count: number
}

type AgentSessionListResponse = {
  data: AgentSession[]
  count: number
}

type AgentProfile = {
  id: string
  name: string
  avatar_url: string | null
  model_name: string | null
  is_default: boolean
  skills: { id: string; enabled: boolean }[]
  mcps: { id: string; enabled: boolean }[]
  files: { id: string }[]
}

type ArtifactItem = {
  path: string
  sizeBytes: number | null
  url: string | null
}

type HomePreviewCard = {
  workflow: WorkflowsListItem
  href: string
  previewUrl: string
  previewFormat: 'cif' | 'pdb'
}

const MAX_RECENT = 3

function sessionStatusDot(status: string) {
  const s = String(status || '').toUpperCase()
  if (s === 'PENDING') return 'bg-amber-500 animate-pulse'
  if (s === 'PROVISIONING') return 'bg-blue-400 animate-pulse'
  if (s === 'RUNNING') return 'bg-blue-500'
  if (s === 'STOPPING') return 'bg-yellow-500'
  if (s === 'STOPPED') return 'bg-muted-foreground/40'
  if (s === 'FAILED') return 'bg-red-500'
  return 'bg-amber-500'
}

function jobStatusDot(status: string) {
  const s = String(status || '').toUpperCase()
  if (s === 'COMPLETED' || s === 'SUCCEEDED' || s === 'SUCCESS') return 'bg-emerald-500'
  if (s === 'FAILED' || s === 'ERROR') return 'bg-red-500'
  if (s === 'RUNNING') return 'bg-blue-500'
  if (s === 'QUEUED' || s === 'PENDING' || s === 'INITIALIZED') return 'bg-yellow-500'
  if (s === 'DRAFT') return 'bg-muted-foreground/40'
  if (s === 'STOPPED') return 'bg-muted-foreground/40'
  return 'bg-amber-500'
}

function statusLabel(status: string) {
  const s = String(status || '').trim()
  if (!s) return 'Unknown'
  return s
    .toLowerCase()
    .split('_')
    .map((part) => (part ? part[0]!.toUpperCase() + part.slice(1) : ''))
    .join(' ')
}

function formatSessionCompute(session: AgentSession) {
  const cpuUnits = Number(session.cpu_units || 0)
  const memoryMiB = Number(session.memory_mib || 0)
  const gpuModel = String(session.gpu_model || '').trim()

  const cpuVcpu = cpuUnits > 0 ? cpuUnits / 1024 : null
  const memoryGb = memoryMiB > 0 ? memoryMiB / 1024 : null

  const parts: string[] = []
  if (cpuVcpu != null) parts.push(`${cpuVcpu % 1 === 0 ? cpuVcpu.toFixed(0) : cpuVcpu.toFixed(1)} vCPU`)
  if (memoryGb != null) parts.push(`${memoryGb % 1 === 0 ? memoryGb.toFixed(0) : memoryGb.toFixed(1)} GB`)
  if (gpuModel) parts.push(gpuModel.replaceAll('_', ' ').toUpperCase())

  return parts.length > 0 ? parts.join(' · ') : null
}

const AURA_BARS = [
  { mono: 'M13 149l0 0c-7,0 -14,6 -14,13l0 27c0,7 6,14 14,14l0 0c7,0 13,-6 13,-14l0 -27c0,-7 -6,-13 -13,-13z', color: '#DB0096' },
  { mono: 'M54 81l0 0c-7,0 -14,6 -14,14l0 68c0,7 6,13 14,13l0 0c7,0 13,-6 13,-13l0 -68c0,-7 -6,-14 -13,-14z', color: '#E56464' },
  { mono: 'M95 41l0 0c-7,0 -14,6 -14,14l0 95c0,7 6,13 14,13l0 0c7,0 14,-6 14,-13l0 -95c0,-7 -6,-14 -14,-14z', color: '#FFB12F' },
  { mono: 'M135 75l0 0c-7,0 -14,6 -14,14l0 95c0,7 6,14 14,14l0 0c7,0 13,-6 13,-14l0 -95c0,-7 -6,-14 -13,-14z', color: '#89CC67' },
  { mono: 'M176 108l0 0c-7,0 -14,6 -14,13l0 95c0,7 6,14 14,14l0 0c7,0 14,-6 14,-14l0 -95c0,-7 -6,-13 -14,-13z', color: '#13D8A3' },
  { mono: 'M217 95l0 0c-7,0 -13,6 -13,14l0 69c0,7 6,13 13,13l0 0c7,0 14,-6 14,-13l0 -69c0,-7 -6,-14 -14,-14z', color: '#15A3D0' },
  { mono: 'M257 68l0 0c-7,0 -13,6 -13,14l0 27c0,7 6,13 13,13l0 0c7,0 14,-6 14,-13l0 -27c0,-7 -6,-14 -14,-14z', color: '#1678F1' },
] as const

function AuraIconAnimated({ className }: { className?: string }) {
  const [hovered, setHovered] = useState(false)

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 271 271"
      aria-label="Aura"
      role="img"
      className={className}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <style>{`
        @keyframes aura-bar-pulse {
          0%, 100% { opacity: 0.45; transform: scaleY(1); }
          50% { opacity: 1; transform: scaleY(1.08); }
        }
      `}</style>
      <g>
        {AURA_BARS.map((bar, i) => (
          <g key={i}>
            <path
              d={bar.mono}
              fill="currentColor"
              style={{
                transformOrigin: 'center',
                animation: 'aura-bar-pulse 2.4s ease-in-out infinite',
                animationDelay: `${i * 180}ms`,
              }}
            />
            <path
              d={bar.mono}
              fill={bar.color}
              style={{
                opacity: hovered ? 1 : 0,
                transitionProperty: 'opacity',
                transitionDuration: '170ms',
                transitionTimingFunction: 'ease-out',
                transitionDelay: hovered ? `${i * 55}ms` : '0ms',
                transformOrigin: 'center',
                animation: hovered ? 'none' : undefined,
              }}
            />
          </g>
        ))}
      </g>
    </svg>
  )
}

const WORKFLOW_TYPE_LABELS: Record<string, string> = {
  fold: 'Fold',
  design: 'Design',
  properties: 'Properties',
  dock: 'Dock',
}

function workflowIcon(type?: string | null) {
  const t = String(type || '').toLowerCase()
  if (t === 'boltzgen_v1') return GitFork
  if (t === 'calvados_openmm_v1') return GitFork
  if (t === 'openmmdl_v1') return GitFork
  if (t === 'evolla_v1') return GitFork
  if (t === 'fold_job') return Atom
  if (t === 'peptide_drug_discovery') return Microscope
  if (t === 'denovo_sequence_generation') return PencilRuler
  return GitFork
}

function workflowLabel(type?: string | null) {
  const t = String(type || '').toLowerCase()
  if (t === 'boltzgen_v1') return 'Workflows'
  if (t === 'calvados_openmm_v1') return 'Workflows'
  if (t === 'openmmdl_v1') return 'Workflows'
  if (t === 'evolla_v1') return 'Workflows'
  if (t === 'fold_job') return 'Fold'
  if (t === 'peptide_drug_discovery') return 'Properties'
  if (t === 'denovo_sequence_generation') return 'Design'
  return String(type || 'Workflow')
}

function isLikelySequenceInput(value?: string | null) {
  const raw = String(value || '').trim()
  if (!raw) return false
  const compact = raw.replace(/\s+/g, '')
  return compact.length >= 20 && /^[ACDEFGHIKLMNPQRSTVWYBXZJUO]+$/i.test(compact)
}

function workflowDisplayTitle(item: WorkflowsListItem) {
  const label = String(item.label || '').trim()
  const type = String(item.workflow_type || '').toLowerCase()
  if (type === 'peptide_drug_discovery') return workflowLabel(item.workflow_type)
  if (label && !isLikelySequenceInput(label)) return label
  const targetName = String(item.target_name || item.target_id || '').trim()
  if (targetName) return targetName
  return workflowLabel(item.workflow_type)
}

function shouldInvertAppLogoInDarkMode(appName?: string | null, appAvatarUrl?: string | null) {
  const name = String(appName || '').trim().toLowerCase()
  const avatar = String(appAvatarUrl || '').trim().toLowerCase()
  return name === 'boltzgen' || avatar.includes('boltzgen')
}

function recentJobPath(item: WorkflowsListItem): string | null {
  if (!item.workflow_id) return null
  const workflowTypeLower = String(item.workflow_type || '').toLowerCase()
  if (workflowTypeLower === 'boltzgen_v1') {
    return `/workflow/composer/${item.workflow_id}`
  }
  if (workflowTypeLower === 'fold_job') {
    const runId = String((item as any).job_run_id || '').trim()
    if (!runId) return null
    return `/sequences/${item.workflow_id}/${runId}`
  }
  if (workflowTypeLower === 'denovo_sequence_generation') {
    return `/tasks/results/${item.workflow_id}`
  }
  if (workflowTypeLower === 'peptide_drug_discovery') {
    return `/tasks/results/${item.workflow_id}`
  }
  if (workflowTypeLower === 'evolla_v1') {
    return `/evolla/results/${item.workflow_id}`
  }
  if (workflowTypeLower === 'calvados_openmm_v1') {
    return `/openmm/results/${item.workflow_id}`
  }
  if (workflowTypeLower === 'openmmdl_v1') {
    return `/openmmdl/results/${item.workflow_id}`
  }
  return null
}

function isCompletedLikeStatus(status?: string | null) {
  const s = String(status || '').trim().toUpperCase()
  return s === 'COMPLETED' || s === 'SUCCEEDED' || s === 'SUCCESS'
}

function isHomePreviewWorkflowType(workflowType?: string | null) {
  const t = String(workflowType || '').trim().toLowerCase()
  return t === 'fold_job' || t === 'calvados_openmm_v1' || t === 'openmmdl_v1' || t === 'boltzgen_v1'
}

function inferStructureFormat(urlOrFileName: string): 'cif' | 'pdb' {
  const lower = String(urlOrFileName || '').trim().toLowerCase()
  return lower.endsWith('.pdb') ? 'pdb' : 'cif'
}

function withTeamId(path: string, teamId?: string | null) {
  const normalizedTeamId = String(teamId || '').trim()
  if (!normalizedTeamId) return path
  try {
    const parsed = new URL(path, typeof window !== 'undefined' ? window.location.origin : 'http://localhost')
    if (!parsed.searchParams.get('team_id')) {
      parsed.searchParams.set('team_id', normalizedTeamId)
    }
    if (/^https?:\/\//i.test(path)) return parsed.toString()
    return `${parsed.pathname}${parsed.search}${parsed.hash}`
  } catch {
    return path
  }
}

function wrapStructureProxy(url: string) {
  return `/api/structure?url=${encodeURIComponent(url)}`
}

function normalizeArtifactPath(path: string) {
  return String(path || '')
    .trim()
    .replace(/^\.?\/*/, '')
}

function fallbackArtifactUrl(logUrl: string | null | undefined, relativePath: string): string | null {
  const cleanRel = normalizeArtifactPath(relativePath)
  if (!cleanRel) return null
  const raw = String(logUrl || '').trim()
  if (!raw) return null
  try {
    const parsed = new URL(raw)
    const pathParts = parsed.pathname.split('/').filter(Boolean)
    if (pathParts.length === 0) return null
    const baseParts = pathParts.slice(0, -1)
    const relParts = cleanRel.split('/').filter(Boolean).map((p) => encodeURIComponent(p))
    parsed.pathname = `/${[...baseParts, ...relParts].join('/')}`
    parsed.search = ''
    parsed.hash = ''
    return parsed.toString()
  } catch {
    return null
  }
}

function resolveArtifactUrl(item: ArtifactItem, logUrl: string | null | undefined): string | null {
  if (item.url) return item.url
  return fallbackArtifactUrl(logUrl, item.path)
}

function pickStructureItem(items: ArtifactItem[]) {
  const pdbs = items.filter((item) => item.path.toLowerCase().endsWith('.pdb'))
  if (pdbs.length === 0) return null
  const nonInputs = pdbs.filter((item) => !item.path.startsWith('_inputs/'))
  const top = nonInputs.find((item) => item.path.toLowerCase().endsWith('/top.pdb') || item.path.toLowerCase() === 'top.pdb')
  if (top) return top
  if (nonInputs[0]) return nonInputs[0]
  return pdbs[0] || null
}

function extractFoldStructureSource(payload: unknown): { url: string; format: 'cif' | 'pdb' } | null {
  const candidates: Array<{ url: string; format: 'cif' | 'pdb' }> = []
  const collect = (value: unknown) => {
    if (!value || typeof value !== 'object') return
    const row = value as Record<string, unknown>
    const cifUrl = String(row.cif_url || row.cifUrl || '').trim()
    const pdbUrl = String(row.pdb_url || row.pdbUrl || '').trim()
    if (cifUrl) candidates.push({ url: cifUrl, format: 'cif' })
    if (pdbUrl) candidates.push({ url: pdbUrl, format: 'pdb' })
  }
  if (payload && typeof payload === 'object') {
    const row = payload as Record<string, unknown>
    collect(row.predictionPayload)
    const sequences = Array.isArray(row.sequences) ? row.sequences : []
    for (const sequence of sequences) {
      if (sequence && typeof sequence === 'object') {
        collect((sequence as Record<string, unknown>).predictionPayload)
      }
    }
  }
  return candidates[0] || null
}

async function fetchJsonWithAuth(path: string): Promise<unknown> {
  const response = await fetchWithAuth(path, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`)
  }
  return response.json()
}

const NEWS_ITEMS = [
  { title: 'OpenFold 3', desc: 'Predict biomolecular structures with proteins, nucleic acids, and ligands.' },
  { title: 'Agent CLI', desc: 'Run autonomous drug discovery research from your terminal.' },
  { title: 'Agents IDE', desc: 'Manage agent sessions in a shared workspace context.' },
  { title: 'Projects Dashboard', desc: 'Organize targets, workflows, and candidates.' },
  { title: 'Workflows', desc: 'Run experimental designs with the visual editor.' },
]

const FEATURED_VIDEO_ID = 'SSvt4h0YaPo'
const FEATURED_VIDEO_EMBED_URL = `https://www.youtube-nocookie.com/embed/${FEATURED_VIDEO_ID}?autoplay=1&rel=0`
const FEATURED_VIDEO_THUMBNAIL = '/news/ai-agents-protein-design-md.png'

export default function HomePage() {
  const router = useRouter()
  const { user } = useAuth()
  const { activeTeamId } = useWorkspace()
  const { openModal } = useNewsModal()
  const { hasAccess, isLoading: featuresLoading } = useFeatureAccessBatch(['projects', 'agent_profiles', 'agent_environment'])
  const firstName = user?.traits?.name?.first || ''
  const push = useCallback((url: string) => router.push(appendWorkspaceScopeToPath(url, activeTeamId)), [router, activeTeamId])

  const [profiles, setProfiles] = useState<AgentProfile[]>([])
  const [loadingProfiles, setLoadingProfiles] = useState(true)
  const [sessions, setSessions] = useState<AgentSession[]>([])
  const [jobs, setJobs] = useState<WorkflowsListItem[]>([])
  const [projects, setProjects] = useState<ProjectItem[]>([])
  const [loadingSessions, setLoadingSessions] = useState(true)
  const [loadingJobs, setLoadingJobs] = useState(true)
  const [loadingProjects, setLoadingProjects] = useState(true)
  const [previewCards, setPreviewCards] = useState<HomePreviewCard[]>([])
  const [loadingPreviewCards, setLoadingPreviewCards] = useState(true)
  const [showFeaturedVideo, setShowFeaturedVideo] = useState(false)

  useEffect(() => {
    if (!API_URL || featuresLoading) return
    if (!hasAccess('agent_profiles')) { setProfiles([]); setLoadingProfiles(false); return }
    setLoadingProfiles(true)
    const params = new URLSearchParams()
    if (activeTeamId) params.set('team_id', activeTeamId)
    fetchWithAuth(`${API_URL}/v1/agent-profiles?${params}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    })
      .then(async (res) => {
        if (!res.ok) { setProfiles([]); return }
        const payload = await res.json()
        const data = payload.data ?? payload
        setProfiles(Array.isArray(data) ? data.slice(0, MAX_RECENT) : [])
      })
      .catch(() => setProfiles([]))
      .finally(() => setLoadingProfiles(false))
  }, [activeTeamId, featuresLoading, hasAccess])

  useEffect(() => {
    if (!API_URL || featuresLoading) return
    if (!hasAccess('agent_environment')) { setSessions([]); setLoadingSessions(false); return }
    setLoadingSessions(true)
    const params = new URLSearchParams()
    if (activeTeamId) params.set('team_id', activeTeamId)
    const q = params.toString()
    fetchWithAuth(`${API_URL}/v1/agent-sessions${q ? `?${q}` : ''}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    })
      .then(async (res) => {
        if (!res.ok) { setSessions([]); return }
        const payload = (await res.json()) as AgentSessionListResponse
        setSessions((payload.data || []).slice(0, MAX_RECENT))
      })
      .catch(() => setSessions([]))
      .finally(() => setLoadingSessions(false))
  }, [activeTeamId, featuresLoading, hasAccess])

  useEffect(() => {
    let cancelled = false

    const loadPreviewCards = async () => {
      if (!API_URL) {
        setJobs([])
        setLoadingJobs(false)
        setPreviewCards([])
        setLoadingPreviewCards(false)
        return
      }

      setLoadingJobs(true)
      setLoadingPreviewCards(true)
      setPreviewCards([])
      try {
        const listResponse = await getWorkflowsList({ limit: MAX_HOME_PREVIEW_CANDIDATES })
        const allRows = Array.isArray(listResponse?.data) ? listResponse.data : []
        if (!cancelled) {
          setJobs(allRows.slice(0, MAX_RECENT))
          setLoadingJobs(false)
        }
        const candidates = allRows.filter(
          (item) => isCompletedLikeStatus(item.status) && isHomePreviewWorkflowType(item.workflow_type)
        )

        const resolvePreviewCard = async (item: WorkflowsListItem): Promise<HomePreviewCard | null> => {
          const href = appendWorkspaceScopeToPath(
            recentJobPath(item) || `/jobs?workflow_id=${item.workflow_id}`,
            activeTeamId
          )
          const type = String(item.workflow_type || '').trim().toLowerCase()

          try {
            if (type === 'fold_job') {
              const params = new URLSearchParams()
              if (activeTeamId) params.set('team_id', activeTeamId)
              const payload = await fetchJsonWithAuth(
                `${API_URL}/v1/jobs/${encodeURIComponent(item.workflow_id)}${params.toString() ? `/results?${params.toString()}` : '/results'}`
              )
              const source = extractFoldStructureSource(payload)
              if (!source?.url) return null
              return {
                workflow: item,
                href,
                previewUrl: withTeamId(wrapStructureProxy(source.url), activeTeamId),
                previewFormat: source.format,
              }
            }

            if (type === 'calvados_openmm_v1' || type === 'openmmdl_v1') {
              const params = new URLSearchParams()
              if (activeTeamId) params.set('team_id', activeTeamId)
              const workflow = (await fetchJsonWithAuth(
                `${API_URL}/v1/workflows/${encodeURIComponent(item.workflow_id)}${params.toString() ? `?${params.toString()}` : ''}`
              )) as Record<string, unknown>
              const tasks = Array.isArray(workflow.tasks) ? (workflow.tasks as Array<Record<string, unknown>>) : []
              const latestTask = tasks.length > 0 ? tasks[tasks.length - 1] : null
              const taskResult =
                latestTask && typeof latestTask.result_raw_json === 'object' && latestTask.result_raw_json
                  ? (latestTask.result_raw_json as Record<string, unknown>)
                  : {}
              const rawArtifacts = Array.isArray(taskResult.artifacts)
                ? (taskResult.artifacts as Array<Record<string, unknown>>)
                : []
              const artifactItems = rawArtifacts
                .map((raw) => {
                  const path = normalizeArtifactPath(String(raw.path || ''))
                  if (!path) return null
                  const size = typeof raw.sizeBytes === 'number' && Number.isFinite(raw.sizeBytes) ? raw.sizeBytes : null
                  const url = String(raw.url || '').trim() || null
                  return { path, sizeBytes: size, url } satisfies ArtifactItem
                })
                .filter(Boolean) as ArtifactItem[]
              const structureItem = pickStructureItem(artifactItems)
              const structureSourceUrl = structureItem
                ? resolveArtifactUrl(structureItem, String(workflow.log_file_url || '').trim() || null)
                : null
              if (!structureSourceUrl) return null
              return {
                workflow: item,
                href,
                previewUrl: withTeamId(wrapStructureProxy(structureSourceUrl), activeTeamId),
                previewFormat: 'pdb',
              }
            }

            if (type === 'boltzgen_v1') {
              const params = new URLSearchParams()
              if (activeTeamId) params.set('team_id', activeTeamId)
              const response = (await fetchJsonWithAuth(
                `${API_URL}/v1/workflows/task-results/${encodeURIComponent(item.workflow_id)}${params.toString() ? `?${params.toString()}` : ''}`
              )) as Record<string, unknown>
              const tasksResults = Array.isArray(response.tasksResults)
                ? (response.tasksResults as Array<Record<string, unknown>>)
                : []
              const boltzgenRows = tasksResults
                .filter((task) => String(task.task_type || '').trim() === 'pipeline_run_boltzgen_v1')
                .flatMap((task) => (Array.isArray(task.parsed_results) ? task.parsed_results : []))
                .filter((row): row is Record<string, unknown> => Boolean(row && typeof row === 'object'))
                .map((row) => {
                  const file = row.file && typeof row.file === 'object' ? (row.file as Record<string, unknown>) : {}
                  return {
                    finalRank: typeof row.final_rank === 'number' ? row.final_rank : Number(row.final_rank),
                    libraryItemId: String(file.libraryItemId || '').trim(),
                    fileName: String(file.fileName || '').trim(),
                  }
                })
                .filter((row) => row.libraryItemId && row.fileName && row.fileName.toLowerCase().endsWith('.cif'))
                .sort((a, b) => {
                  const left = Number.isFinite(a.finalRank) ? a.finalRank : Number.POSITIVE_INFINITY
                  const right = Number.isFinite(b.finalRank) ? b.finalRank : Number.POSITIVE_INFINITY
                  return left - right
                })
              const topRow = boltzgenRows[0]
              if (!topRow) return null
              return {
                workflow: item,
                href,
                previewUrl: withTeamId(
                  `/api/structure?itemId=${encodeURIComponent(topRow.libraryItemId)}&fileName=${encodeURIComponent(topRow.fileName)}`,
                  activeTeamId
                ),
                previewFormat: inferStructureFormat(topRow.fileName),
              }
            }
          } catch {
            // Skip rows that cannot be hydrated into a reliable preview.
          }
          return null
        }

        const resolvedCards = new Map<number, HomePreviewCard>()
        const publishResolvedCards = () => {
          if (cancelled) return
          const ordered = Array.from(resolvedCards.entries())
            .sort((left, right) => left[0] - right[0])
            .map(([, card]) => card)
            .slice(0, MAX_HOME_PREVIEW_CARDS)
          setPreviewCards(ordered)
        }

        await Promise.all(
          candidates.map(async (item, index) => {
            const card = await resolvePreviewCard(item)
            if (!card || cancelled) return
            resolvedCards.set(index, card)
            publishResolvedCards()
          })
        )

        publishResolvedCards()
      } catch {
        if (!cancelled) {
          setJobs([])
          setLoadingJobs(false)
          setPreviewCards([])
        }
      } finally {
        if (!cancelled) {
          setLoadingJobs(false)
          setLoadingPreviewCards(false)
        }
      }
    }

    void loadPreviewCards()
    return () => {
      cancelled = true
    }
  }, [activeTeamId])

  useEffect(() => {
    if (!API_URL || featuresLoading) return
    if (!hasAccess('projects')) { setProjects([]); setLoadingProjects(false); return }
    setLoadingProjects(true)
    const params = new URLSearchParams({ limit: String(MAX_RECENT) })
    if (activeTeamId) params.set('team_id', activeTeamId)
    fetchWithAuth(`${API_URL}/v1/projects/list?${params.toString()}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'include',
    })
      .then(async (res) => {
        if (!res.ok) { setProjects([]); return }
        const json = (await res.json()) as ProjectListResponse
        setProjects((json.data || []).slice(0, MAX_RECENT))
      })
      .catch(() => setProjects([]))
      .finally(() => setLoadingProjects(false))
  }, [activeTeamId, featuresLoading, hasAccess])

  const allQuickActions = [
    { label: 'New Agent', icon: Pyramid, action: () => push('/agent/profiles?create=1'), feature: 'agent_profiles' },
    { label: 'New Project', icon: Plus, action: () => push('/projects/new'), feature: 'projects' },
    { label: 'Browse Apps', icon: Store, action: () => push('/apps') },
    { label: 'Launch Environment', icon: Cpu, action: () => push('/agent/environment'), feature: 'agent_environment' },
    { label: 'Browse Skills', icon: Sparkles, action: () => push('/agent/skills') },
    { label: 'Agent CLI', icon: Terminal, action: () => push('/agent/cli') },
  ]
  const quickActions = allQuickActions.filter((qa) => !qa.feature || hasAccess(qa.feature))

  return (
    <div className="flex flex-col w-full max-w-5xl mx-auto px-4 md:px-6 py-8 md:py-12 gap-10">
      {/* Hero */}
      <div className="flex flex-col items-center text-center pt-4 pb-2 gap-3">
        <AuraIconAnimated className="h-10 w-10 text-muted-foreground/60" />
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground">
          Let&apos;s do science
        </h1>
        <p className="text-sm text-muted-foreground max-w-md">
          Pick up where you left off or start something new.
        </p>
      </div>

      {/* Quick Actions */}
      <div className={cn(
        'grid gap-2',
        quickActions.length <= 3 ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-6',
      )}>
        {quickActions.map((qa) => (
          <button
            key={qa.label}
            onClick={qa.action}
            className="group flex flex-col items-center gap-2 rounded-xl border border-border/50 bg-card px-3 py-4 text-center transition-all hover:border-border hover:bg-accent/50"
          >
            <qa.icon className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
            <span className="text-xs font-medium text-muted-foreground group-hover:text-foreground transition-colors">
              {qa.label}
            </span>
          </button>
        ))}
      </div>

      {/* Featured Video */}
      <section className="rounded-xl border border-border/50 bg-card">
        <button
          type="button"
          onClick={() => setShowFeaturedVideo(true)}
          className="w-full text-left p-3 sm:p-4 flex flex-col sm:flex-row gap-4 sm:items-center hover:bg-accent/30 transition-colors rounded-xl"
        >
          <div className="relative w-full sm:w-48 md:w-56 aspect-video overflow-hidden rounded-lg border border-border/50 bg-muted/30 shrink-0">
            <img
              src={FEATURED_VIDEO_THUMBNAIL}
              alt="AI Agents for Protein Design and Molecular Dynamics"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-black/25 flex items-center justify-center">
              <span className="h-12 w-12 rounded-full bg-black/70 text-white flex items-center justify-center border border-white/20">
                <Play className="h-5 w-5 fill-current translate-x-0.5" />
              </span>
            </div>
          </div>
          <div className="min-w-0">
            <h2 className="text-sm sm:text-base font-medium text-foreground">
              AI Agents for Protein Design and Molecular Dynamics
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              See how Fastfold lets scientists and AI agents collaborate to run complex protein and drug discovery
              workflows.
            </p>
            <p className="text-xs text-muted-foreground mt-2">3 minutes</p>
          </div>
        </button>
      </section>

      <Dialog open={showFeaturedVideo} onOpenChange={setShowFeaturedVideo}>
        <DialogContent className="max-w-[95vw] md:max-w-5xl border-none bg-transparent p-0 shadow-none">
          <DialogTitle className="sr-only">AI Agents for Protein Design and Molecular Dynamics</DialogTitle>
          <div className="aspect-video w-full overflow-hidden rounded-xl bg-black">
            <iframe
              className="h-full w-full"
              src={FEATURED_VIDEO_EMBED_URL}
              title="AI Agents for Protein Design and Molecular Dynamics"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Your Agents */}
      {hasAccess('agent_profiles') && (
        <section className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Pyramid className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-sm font-medium text-foreground">Your Agents</h2>
            </div>
            <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={() => push('/agent/profiles')}>
              View all <ArrowRight className="ml-1 h-3 w-3" />
            </Button>
          </div>
          <div className="rounded-xl border border-border/50 bg-card divide-y divide-border/50">
            {loadingProfiles ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3">
                  <Skeleton className="h-8 w-8 rounded-lg" />
                  <div className="flex flex-col gap-1">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-3 w-40" />
                  </div>
                </div>
              ))
            ) : profiles.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                No agent profiles yet.{' '}
                <button className="text-foreground underline underline-offset-2" onClick={() => push('/agent/profiles')}>
                  Create one
                </button>
              </div>
            ) : (
              profiles.map((profile) => {
                const enabledSkills = profile.skills?.filter((s) => s.enabled).length ?? 0
                const enabledMcps = profile.mcps?.filter((m) => m.enabled).length ?? 0
                const fileCount = profile.files?.length ?? 0
                return (
                  <button
                    key={profile.id}
                    onClick={() => push(`/agent/profiles/${profile.id}`)}
                    className="flex items-center gap-3 px-4 py-3 w-full text-left hover:bg-accent/30 transition-colors"
                  >
                    <Avatar className="h-8 w-8 rounded-lg shrink-0">
                      {profile.avatar_url ? (
                        <AvatarImage src={profile.avatar_url} alt={profile.name} />
                      ) : null}
                      <AvatarFallback className="rounded-lg bg-muted">
                        <Bot className="h-4 w-4 text-muted-foreground" />
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">{profile.name}</span>
                        {profile.is_default && (
                          <Badge variant="secondary" className="text-[10px] shrink-0">Default</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {profile.model_name ?? 'No model set'}
                        {enabledSkills > 0 && ` · ${enabledSkills} skill${enabledSkills > 1 ? 's' : ''}`}
                        {enabledMcps > 0 && ` · ${enabledMcps} MCP${enabledMcps > 1 ? 's' : ''}`}
                        {fileCount > 0 && ` · ${fileCount} file${fileCount > 1 ? 's' : ''}`}
                      </p>
                    </div>
                  </button>
                )
              })
            )}
          </div>
        </section>
      )}

      {/* Recent Agent Sessions */}
      {hasAccess('agent_environment') && (
        <section className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Cpu className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-sm font-medium text-foreground">Recent Sessions</h2>
            </div>
            <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={() => push('/agent/environment')}>
              View all <ArrowRight className="ml-1 h-3 w-3" />
            </Button>
          </div>
          <div className="rounded-xl border border-border/50 bg-card divide-y divide-border/50">
            {loadingSessions ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-32 ml-auto" />
                </div>
              ))
            ) : sessions.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                No agent sessions yet.{' '}
                <button className="text-foreground underline underline-offset-2" onClick={() => push('/agent/environment')}>
                  Launch one
                </button>
              </div>
            ) : (
              sessions.map((s) => {
                const compute = formatSessionCompute(s)
                return (
                  <button
                    key={s.id}
                    onClick={() => push('/agent/environment')}
                    className="flex items-center gap-3 px-4 py-3 w-full text-left hover:bg-accent/30 transition-colors"
                  >
                    <span className={cn('h-2.5 w-2.5 rounded-full shrink-0', sessionStatusDot(s.status))} />
                    <span className="text-[13px] font-medium text-foreground/80">{statusLabel(s.status)}</span>
                    {s.profile_name && (
                      <span className="text-sm text-foreground truncate">{s.profile_name}</span>
                    )}
                    {compute && (
                      <span className="text-xs text-muted-foreground truncate hidden sm:inline">{compute}</span>
                    )}
                    {s.runtime && (
                      <span className="text-xs text-muted-foreground hidden sm:inline">{s.runtime}</span>
                    )}
                    <span className="ml-auto text-xs text-muted-foreground shrink-0">
                      {formatRelative(s.updated_at || s.created_at)}
                    </span>
                  </button>
                )
              })
            )}
          </div>
        </section>
      )}

      {/* Recent Jobs */}
      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Logs className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-medium text-foreground">Recent Jobs</h2>
          </div>
          <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={() => push('/jobs')}>
            View all <ArrowRight className="ml-1 h-3 w-3" />
          </Button>
        </div>
        <div className="rounded-xl border border-border/50 bg-card divide-y divide-border/50">
          {loadingJobs ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-32 ml-auto" />
              </div>
            ))
          ) : jobs.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">
              No jobs yet.{' '}
              <button className="text-foreground underline underline-offset-2" onClick={() => push('/workflows/templates')}>
                Start a workflow
              </button>
            </div>
          ) : (
            jobs.map((j) => {
              const href = appendWorkspaceScopeToPath(
                recentJobPath(j) || `/jobs?workflow_id=${j.workflow_id}`,
                activeTeamId
              )
              return (
                <Link
                  key={j.workflow_id}
                  href={href}
                  className="flex items-center gap-3 px-4 py-3 w-full text-left hover:bg-accent/30 transition-colors"
                >
                  <span className={cn('h-2.5 w-2.5 rounded-full shrink-0', jobStatusDot(j.status))} />
                  <span className="text-[13px] font-medium text-foreground/80 shrink-0">{statusLabel(j.status)}</span>
                  <div className="min-w-0 flex flex-1 items-center gap-2">
                    <span className="truncate text-sm font-medium text-foreground">
                      {workflowDisplayTitle(j)}
                    </span>
                    <span className="inline-flex min-w-0 shrink items-center gap-1.5 text-xs text-muted-foreground">
                      <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center overflow-hidden rounded-sm bg-muted/60 text-muted-foreground">
                        {j.app_avatar_url ? (
                          <img
                            src={j.app_avatar_url}
                            alt={j.app_name || 'App'}
                            className={cn(
                              'h-full w-full rounded-sm object-cover',
                              shouldInvertAppLogoInDarkMode(j.app_name, j.app_avatar_url) && 'dark:invert'
                            )}
                          />
                        ) : (() => {
                          const Icon = workflowIcon(j.workflow_type)
                          return <Icon className="h-2.5 w-2.5" />
                        })()}
                      </span>
                      <span className="truncate">{j.app_name || workflowLabel(j.workflow_type)}</span>
                    </span>
                  </div>
                  <span className="ml-auto text-xs text-muted-foreground shrink-0">
                    {formatRelative(j.updated_at || j.created_at)}
                  </span>
                </Link>
              )
            })
          )}
        </div>
      </section>

      {loadingPreviewCards || previewCards.length > 0 ? (
        <section className="flex flex-col gap-3">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {previewCards.map((card) => (
              <Link
                key={card.workflow.workflow_id}
                href={card.href}
                className="group overflow-hidden rounded-xl border border-border/50 bg-card transition-colors hover:bg-accent/20"
              >
                <div className="aspect-square w-full bg-muted/20">
                  <Py2dMolMiniPreview
                    url={card.previewUrl}
                    format={card.previewFormat}
                    viewerId={`home-preview-${card.workflow.workflow_id}`}
                  />
                </div>
                <div className="space-y-2 px-3 py-3">
                  <div className="flex items-center gap-2">
                    <span className={cn('h-2.5 w-2.5 rounded-full shrink-0', jobStatusDot(card.workflow.status))} />
                    <span className="text-[11px] font-medium text-foreground/80">
                      {statusLabel(card.workflow.status)}
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      {formatRelative(card.workflow.updated_at || card.workflow.created_at)}
                    </span>
                  </div>
                  <div className="line-clamp-2 text-sm font-medium text-foreground">
                    {workflowDisplayTitle(card.workflow)}
                  </div>
                  <div className="inline-flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
                    <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center overflow-hidden rounded-sm bg-muted/60 text-muted-foreground">
                      {card.workflow.app_avatar_url ? (
                        <img
                          src={card.workflow.app_avatar_url}
                          alt={card.workflow.app_name || 'App'}
                          className={cn(
                            'h-full w-full rounded-sm object-cover',
                            shouldInvertAppLogoInDarkMode(card.workflow.app_name, card.workflow.app_avatar_url) && 'dark:invert'
                          )}
                        />
                      ) : (() => {
                        const Icon = workflowIcon(card.workflow.workflow_type)
                        return <Icon className="h-2.5 w-2.5" />
                      })()}
                    </span>
                    <span className="truncate">
                      {card.workflow.app_name || workflowLabel(card.workflow.workflow_type)}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
            {loadingPreviewCards &&
              Array.from({ length: Math.max(0, MAX_HOME_PREVIEW_CARDS - previewCards.length) }).map((_, i) => (
                <div key={i} className="overflow-hidden rounded-xl border border-border/50 bg-card">
                  <Skeleton className="aspect-square w-full" />
                  <div className="space-y-2 px-3 py-3">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              ))}
          </div>
        </section>
      ) : null}

      {/* Recent Projects */}
      {hasAccess('projects') && (
        <section className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <LayoutDashboard className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-sm font-medium text-foreground">Recent Projects</h2>
            </div>
            <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={() => push('/projects')}>
              View all <ArrowRight className="ml-1 h-3 w-3" />
            </Button>
          </div>
          <div className="rounded-xl border border-border/50 bg-card divide-y divide-border/50">
            {loadingProjects ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-32 ml-auto" />
                </div>
              ))
            ) : projects.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                No projects yet.{' '}
                <button className="text-foreground underline underline-offset-2" onClick={() => push('/projects/new')}>
                  Create one
                </button>
              </div>
            ) : (
              projects.map((p) => (
                <button
                  key={p.id}
                  onClick={() => push(`/projects/${p.id}`)}
                  className="flex items-center gap-3 px-4 py-3 w-full text-left hover:bg-accent/30 transition-colors"
                >
                  <span className="text-sm text-foreground truncate">{p.title}</span>
                  {typeof p.targets_count === 'number' && p.targets_count > 0 && (
                    <span className="text-xs text-muted-foreground hidden sm:inline">
                      {p.targets_count} target{p.targets_count !== 1 ? 's' : ''}
                    </span>
                  )}
                  {typeof p.experiments_count === 'number' && p.experiments_count > 0 && (
                    <span className="text-xs text-muted-foreground hidden sm:inline">
                      {p.experiments_count} workflow{p.experiments_count !== 1 ? 's' : ''}
                    </span>
                  )}
                  <span className="ml-auto text-xs text-muted-foreground shrink-0">
                    {formatRelative(p.updated_at || p.created_at || '')}
                  </span>
                </button>
              ))
            )}
          </div>
        </section>
      )}

      {/* What's New */}
      <section className="flex flex-col gap-3 pb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Gift className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-medium text-foreground">What&apos;s New</h2>
          </div>
          <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={openModal}>
            See all <ArrowRight className="ml-1 h-3 w-3" />
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          {NEWS_ITEMS.map((item) => (
            <button
              key={item.title}
              onClick={openModal}
              className="group flex flex-col gap-2 rounded-xl border border-border/50 bg-card p-4 text-left transition-all hover:border-border hover:bg-accent/30"
            >
              <span className="text-sm font-medium text-foreground">{item.title}</span>
              <p className="text-xs text-muted-foreground line-clamp-2">{item.desc}</p>
            </button>
          ))}
        </div>
      </section>
    </div>
  )
}
